import { load as parseYaml } from 'js-yaml'
import { z } from 'zod'
import {
  DOCUMENT_TYPES,
  type AiSkill,
  type AiSkillType,
  type AiSkillDefinition
} from '@/types'

// Tri par `order` croissant (skills sans ordre à la fin), puis par libellé.
export function sortSkillsByOrder(a: AiSkill, b: AiSkill): number {
  const aOrder = a.order ?? Infinity
  const bOrder = b.order ?? Infinity
  if (aOrder !== bOrder) return aOrder - bOrder
  return (a.label ?? a.id).localeCompare(b.label ?? b.id)
}

// Rétro-compat des skills déjà stockés : `suggestion` -> `general`, `quick` -> `contextual`.
export function normalizeSkillType(type?: string): AiSkillType {
  if (type === 'contextual' || type === 'quick') return 'contextual'
  return 'general'
}

const SkillFrontmatterSchema = z.object({
  id: z.string().min(1),
  label: z.string().optional(),
  labels: z.record(z.string(), z.string()).optional(),
  icon: z.string().optional().default('document-text'),
  type: z.enum(['general', 'contextual', 'quick', 'suggestion']).optional(),
  documentTypes: z.array(z.enum(DOCUMENT_TYPES)).optional(),
  order: z.number().optional(),
  maxSteps: z.number().int().positive().max(40).optional()
})

const FRONTMATTER_REGEX = /^﻿?---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function extractFrontmatter(raw: string): {
  data: Record<string, unknown>
  body: string
} {
  const match = raw.match(FRONTMATTER_REGEX)
  if (!match) return { data: {}, body: raw }
  const parsed = parseYaml(match[1]) ?? {}
  if (!isPlainObject(parsed)) {
    throw new Error('Skill frontmatter must be a YAML object')
  }
  return { data: parsed, body: match[2] ?? '' }
}

export function parseSkillFile(rawContent: string): AiSkillDefinition {
  const { data, body: content } = extractFrontmatter(rawContent)
  const body = content.trim()
  if (!body) throw new Error('Skill content is empty')

  const parsed = SkillFrontmatterSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(`Invalid skill frontmatter: ${parsed.error.message}`)
  }

  const { id, label, labels, icon, type, documentTypes, order, maxSteps } =
    parsed.data
  return {
    id,
    ...(label ? { label } : {}),
    ...(labels ? { labels } : {}),
    icon,
    type: normalizeSkillType(type),
    ...(documentTypes ? { documentTypes } : {}),
    ...(order !== undefined ? { order } : {}),
    ...(maxSteps !== undefined ? { maxSteps } : {}),
    content: body
  }
}
